import React from 'react';
import { calculatePreference } from '../quizData';
import logo from '../TBL Logo White.png';
import './ResultsScreen.css';

// Slider component for preference visualization
const PreferenceSlider = ({ value, maxValue = 100 }) => {
  const percentage = (value / maxValue) * 100;
  
  return (
    <div className="preference-slider">
      <div className="slider-track">
        <div 
          className="slider-fill" 
          style={{ width: `${percentage}%` }}
        ></div>
        <div 
          className="slider-thumb" 
          style={{ left: `${percentage}%` }}
        ></div>
      </div>
      <span className="slider-value">{value}</span>
    </div>
  );
};

const ResultsScreen = ({ answers, recommendations, onRestart, onBackToHome }) => {
  const acidityPreference = calculatePreference(answers, 'acidity');
  const sweetnessPreference = calculatePreference(answers, 'sweetness');
  const bodyPreference = calculatePreference(answers, 'body');

  const getPreferenceLabel = (category, preference) => {
    const labels = {
      acidity: { high: 'High Acidity', medium: 'Medium Acidity', low: 'Neutral Acidity' },
      sweetness: { high: 'High Sweetness', medium: 'Medium Sweetness', low: 'Low Sweetness' },
      body: { full: 'Full Body', medium: 'Medium Body', light: 'Light Body' }
    };
    return labels[category]?.[preference] || preference;
  };

  // Calculate numerical scores (0-100) based on quiz answers
  const calculateNumericalScore = (answers, category) => {
    const categoryAnswers = answers.filter(answer => answer.category === category);
    const values = categoryAnswers.map(answer => answer.value);
    
    // Count occurrences
    const counts = values.reduce((acc, val) => {
      acc[val] = (acc[val] || 0) + 1;
      return acc;
    }, {});
    
    // Calculate weighted score based on answers
    let totalScore = 0;
    let totalWeight = 0;
    
    Object.entries(counts).forEach(([value, count]) => {
      let score;
      if (category === 'body') {
        // Body: light=25, medium=50, full=75
        score = value === 'light' ? 25 : value === 'medium' ? 50 : 75;
      } else {
        // Acidity/Sweetness: low=25, medium=50, high=75
        score = value === 'low' ? 25 : value === 'medium' ? 50 : 75;
      }
      totalScore += score * count;
      totalWeight += count;
    });
    
    // Return average score, rounded to nearest integer
    return Math.round(totalScore / totalWeight);
  };

  const acidityScore = calculateNumericalScore(answers, 'acidity');
  const sweetnessScore = calculateNumericalScore(answers, 'sweetness');
  const bodyScore = calculateNumericalScore(answers, 'body');

  return (
    <div className="results-screen">
      <div className="results-header">
        <h1>Your Wine Profile</h1>
        <p>Based on your preferences, here's what we discovered about your wine taste:</p>
      </div>

      <div className="preferences-table">
        <table>
          <thead>
            <tr>
              <th>Characteristic</th>
              <th>Your Preference</th>
              <th>Level</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Acidity</td>
              <td>{getPreferenceLabel('acidity', acidityPreference)}</td>
              <td><PreferenceSlider value={acidityScore} /></td>
            </tr>
            <tr>
              <td>Sweetness</td>
              <td>{getPreferenceLabel('sweetness', sweetnessPreference)}</td>
              <td><PreferenceSlider value={sweetnessScore} /></td>
            </tr>
            <tr>
              <td>Body</td>
              <td>{getPreferenceLabel('body', bodyPreference)}</td>
              <td><PreferenceSlider value={bodyScore} /></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="recommendations-section">
        <h2>Recommended Wines for You</h2>
        <p>Here are the wines that best match your preferences:</p>
        
        {recommendations && recommendations.length > 0 ? (
          <div className="recommendations-grid">
            {recommendations.map((wine, index) => (
              <div key={index} className="wine-card">
                <h3>{wine.displayName}</h3>
                <div className="wine-details">
                  <p><strong>Color:</strong> {wine.Color || 'N/A'}</p>
                  <p><strong>Varietals:</strong> {wine.Varietals || 'N/A'}</p>
                  <p><strong>Intensity Level:</strong> {wine['Intensity Level'] || 'N/A'}</p>
                </div>
                <div className="wine-scores">
                  <div className="score-item">
                    <span className="score-label">Acidity:</span>
                    <PreferenceSlider value={Math.round(wine['Acidity Index'] || 0)} />
                  </div>
                  <div className="score-item">
                    <span className="score-label">Sweetness:</span>
                    <PreferenceSlider value={Math.round(wine['Sweetness Index'] || 0)} />
                  </div>
                  <div className="score-item">
                    <span className="score-label">Body:</span>
                    <PreferenceSlider value={Math.round(wine['Body Index'] || 0)} />
                  </div>
                </div>
                <div className="match-score">
                  Match Score: {wine.score}/9
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-recommendations">
            <p>No wine recommendations available. Please try taking the quiz again.</p>
            <p>Debug info: Recommendations count: {recommendations ? recommendations.length : 'undefined'}</p>
          </div>
        )}
      </div>

      <div className="action-buttons">
        <button className="restart-button" onClick={onRestart}>
          Take Quiz Again
        </button>
        <button className="home-button" onClick={onBackToHome}>
          Back to Home
        </button>
      </div>

      <div className="footer">
        <div className="footer-separator"></div>
        <img src={logo} className="footer-logo" alt="TBL Logo" />
      </div>
    </div>
  );
};

export default ResultsScreen;
